import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useState, FormEvent, useEffect, useRef } from 'react'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { Helmet } from 'react-helmet-async'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

export default function Estimate() {
  const [squareFootage, setSquareFootage] = useState('')
  const [deckMaterial, setDeckMaterial] = useState('')
  
  const [hasRailing, setHasRailing] = useState('No')
  const [railingFeet, setRailingFeet] = useState('')
  const [railingMaterial, setRailingMaterial] = useState('')
  
  const [hasPrivacyWall, setHasPrivacyWall] = useState('No')
  const [privacyWallFeet, setPrivacyWallFeet] = useState('')
  
  const [hasStairs, setHasStairs] = useState('No')
  const [stairsCount, setStairsCount] = useState('')
  const [stairsMaterial, setStairsMaterial] = useState('')
  
  const [hasSkirting, setHasSkirting] = useState('No')
  const [skirtingFeet, setSkirtingFeet] = useState('')
  const [skirtingMaterial, setSkirtingMaterial] = useState('')
  
  const [hasFence, setHasFence] = useState('No')
  const [fenceFeet, setFenceFeet] = useState('')
  const [fenceMaterial, setFenceMaterial] = useState('')
  
  const [hasGate, setHasGate] = useState('No')
  const [gateCount, setGateCount] = useState('')
  
  const [hasRVGate, setHasRVGate] = useState('No')
  
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  
  const [estimatedPrice, setEstimatedPrice] = useState('$0.00')
  const [monthlyPayment, setMonthlyPayment] = useState('$0.00')
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState(false)
  
  const formRef = useRef<HTMLFormElement>(null)

  // Handle client-side navigation for interlinking
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }
  
  const deckMaterialPrices = {
    'Vinyl decking': 38,
    'Pressure treated wood': 31,
    'Composite': 44.5,
  }
  
  const railingMaterialPrices = {
    'Glass Railing': 54.5,
    'Aluminum Picket': 44,
    'Pressure Treated Wood': 36,
  }
  
  const stairsMaterialPrices = {
    'Composite': 230,
    'Pressure Treated': 130,
  }
  
  const skirtingMaterialPrices = {
    'Lattice': 34,
    'Smart Board': 46,
    'Pressure Treated': 36,
  }
  
  const fenceMaterialPrices = {
    'Pressure Treated 4x4 Posts': 68,
    'Pressure Treated 4x6 Posts': 72,
    'Cedar 4x4 Posts': 80,
    'Cedar 4x6 Posts': 84,
  }
  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);
    
    try {
      const formData = {
        firstName,
        lastName,
        phone,
        email,
        squareFootage,
        deckMaterial,
        hasRailing,
        railingFeet,
        railingMaterial,
        hasPrivacyWall,
        privacyWallFeet,
        hasStairs,
        stairsCount,
        stairsMaterial,
        hasSkirting,
        skirtingFeet,
        skirtingMaterial,
        hasFence,
        fenceFeet,
        fenceMaterial,
        hasGate,
        gateCount,
        hasRVGate,
        estimatedPrice,
        monthlyPayment
      };
      
      const response = await fetch('https://formspree.io/f/xqewgkwz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        toast.success("Estimate submitted successfully!");
        
        // Redirect to the Thank You page
        window.history.pushState({}, '', '/thank-you?form=deck_calculator');
        window.dispatchEvent(new Event('popstate'));
        window.scrollTo(0, 0);
      } else {
        setSubmitError('Failed to submit estimate. Please try again later.');
        toast.error("Failed to submit estimate. Please try again later.");
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again later.');
      toast.error("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  useEffect(() => {
    updateEstimate()
  }, [
    squareFootage, deckMaterial,
    hasRailing, railingFeet, railingMaterial,
    hasPrivacyWall, privacyWallFeet,
    hasStairs, stairsCount, stairsMaterial,
    hasSkirting, skirtingFeet, skirtingMaterial,
    hasFence, fenceFeet, fenceMaterial,
    hasGate, gateCount,
    hasRVGate
  ])
  
  const updateEstimate = () => {
    const sqFt = parseFloat(squareFootage) || 0
    const deckMaterialCost = deckMaterial ? deckMaterialPrices[deckMaterial as keyof typeof deckMaterialPrices] || 0 : 0
    
    const railingLft = hasRailing === 'Yes' ? parseFloat(railingFeet) || 0 : 0
    const railingMaterialCost = railingMaterial ? railingMaterialPrices[railingMaterial as keyof typeof railingMaterialPrices] || 0 : 0
    
    const privacyWallLft = hasPrivacyWall === 'Yes' ? parseFloat(privacyWallFeet) || 0 : 0
    const privacyWallCost = 135
    
    const stairsQty = hasStairs === 'Yes' ? parseInt(stairsCount) || 0 : 0
    const stairsMaterialCost = stairsMaterial ? stairsMaterialPrices[stairsMaterial as keyof typeof stairsMaterialPrices] || 0 : 0
    
    const skirtingLft = hasSkirting === 'Yes' ? parseFloat(skirtingFeet) || 0 : 0
    const skirtingMaterialCost = skirtingMaterial ? skirtingMaterialPrices[skirtingMaterial as keyof typeof skirtingMaterialPrices] || 0 : 0
    
    const fenceLft = hasFence === 'Yes' ? parseFloat(fenceFeet) || 0 : 0
    const fenceMaterialCost = fenceMaterial ? fenceMaterialPrices[fenceMaterial as keyof typeof fenceMaterialPrices] || 0 : 0
    
    const gates = hasGate === 'Yes' ? parseInt(gateCount) || 0 : 0
    const gateCost = 625
    
    const rvGateCost = hasRVGate === 'Yes' ? 950 : 0
    
    const totalCost = 
      (sqFt * deckMaterialCost) + 
      (railingLft * railingMaterialCost) + 
      (privacyWallLft * privacyWallCost) + 
      (stairsQty * stairsMaterialCost) + 
      (skirtingLft * skirtingMaterialCost) + 
      (fenceLft * fenceMaterialCost) + 
      (gates * gateCost) + 
      rvGateCost
    
    setEstimatedPrice(`$${totalCost.toFixed(2)}`)
    setMonthlyPayment(`$${(totalCost * 0.01351).toFixed(2)}`)
  }
  
  const selectClass = "w-full py-2 px-3 border border-gray-300 bg-white rounded-sm focus:outline-none focus:ring-1 focus:ring-beaver-green"
  const inputClass = "w-full py-2 px-3 border border-gray-300 bg-white rounded-sm focus:outline-none focus:ring-1 focus:ring-beaver-green"
  const labelClass = "block text-sm font-medium text-gray-700 mb-1"

  const softwareSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "name": "Busy Beaver Deck Cost Calculator",
        "operatingSystem": "Web",
        "applicationCategory": "ConstructionBusinessApplication",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "CAD"
        },
        "description": "Calculate an estimated deck project cost in Calgary based on deck size, decking material, railing, stairs, privacy walls, skirting, fencing, and gates."
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is the average cost to build a deck in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "residential deck construction in Calgary typically ranges from $31.00 to $55.00+ per square foot. Pressure-treated lumber is the most cost-effective entry point, while high-performance composite like TimberTech offers superior longevity against Alberta's extreme freeze-thaw cycles."
            }
          },
          {
            "@type": "Question",
            "name": "Do I need a building permit for my deck?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "In Calgary, any deck over 0.6 meters (2 feet) above grade or any deck that is structurally attached to the house requires a permit. Our team manages the entire permit application process to ensure your new outdoor space is fully compliant with local bylaws."
            }
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Estimate",
            "item": "https://busybeaverconstruction.com/estimate"
          }
        ]
      }
    ]
  };
  
  return (
    <main>
      <Helmet>
       <title>Deck Cost Calculator Calgary | Online Cost Estimator</title>
       <meta
         name="description"
         content="Calculate your estimated deck cost in Calgary with our free online calculator. Estimate pricing for composite, vinyl, pressure-treated wood, railings, stairs, skirting and more."
       />
        <link rel="canonical" href="https://busybeaverconstruction.com/estimate" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareSchema) }} />

      <Header />
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover />

      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg"
          alt="TimberTech composite deck installation by Busy Beaver Construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover object-bottom loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Calgary's Leading Custom Deck & Fence Builder</p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="container">
          <h1 className="text-center text-5xl lg:text-6xl font-primary mb-6">Calculate Your Deck - Online</h1>
          
          <div className="mb-10 p-8 bg-gray-50 border-l-8 border-beaver-green max-w-4xl mx-auto shadow-sm">
            <p className="text-lg md:text-xl text-text-dark font-text leading-relaxed">
              Our <strong>deck building calculator</strong> provides instant, accurate cost estimates for Calgary homeowners. By utilizing current market pricing for <strong>composite decking</strong> ($44.50/sqft), cedar, and pressure-treated wood ($31.00/sqft), we help you plan your outdoor project with complete budget transparency.
            </p>
          </div>

          <div className="text-center mb-8">
            <p className="text-gray-700 font-text">
              Contact us now to schedule a free onsite estimate. <a href="tel:4032865400" className="text-beaver-green font-bold hover:underline">403-286-5400</a>
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto border border-gray-300 mb-10">
            <div className="bg-white p-6">
              <h2 className="text-xl font-bold text-red-600 mb-4 uppercase">Calculate The Cost of Your Dream Deck!</h2>
              
              <form onSubmit={handleSubmit} ref={formRef}>
                <div className="mb-4">
                  <label className={labelClass}>Deck Square Footage:</label>
                  <div className="flex">
                    <input 
                      type="number" 
                      value={squareFootage}
                      onChange={(e) => setSquareFootage(e.target.value)}
                      className={`${inputClass} mr-2`}
                      placeholder="Value"
                      required
                    />
                    <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                      SQFT
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className={labelClass}>Deck Material:</label>
                  <select 
                    value={deckMaterial}
                    onChange={(e) => setDeckMaterial(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="">Select Material</option>
                    <option value="Vinyl decking">Vinyl decking</option>
                    <option value="Pressure treated wood">Pressure treated wood</option>
                    <option value="Composite">Composite</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className={labelClass}>Railing:</label>
                  <select 
                    value={hasRailing}
                    onChange={(e) => setHasRailing(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {hasRailing === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-200">
                    <label className={labelClass}>Railing Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={railingFeet}
                        onChange={(e) => setRailingFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                        required
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                    
                    <label className={`${labelClass} mt-2`}>Railing Material:</label>
                    <select 
                      value={railingMaterial}
                      onChange={(e) => setRailingMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Material</option>
                      <option value="Glass Railing">Glass Railing</option>
                      <option value="Aluminum Picket">Aluminum Picket</option>
                      <option value="Pressure Treated Wood">Pressure Treated Wood</option>
                    </select>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className={labelClass}>Privacy Wall (6' High):</label>
                  <select 
                    value={hasPrivacyWall}
                    onChange={(e) => setHasPrivacyWall(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {hasPrivacyWall === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-200">
                    <label className={labelClass}>Privacy Wall Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={privacyWallFeet}
                        onChange={(e) => setPrivacyWallFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className={labelClass}>Stairs (4' Wide):</label>
                  <select 
                    value={hasStairs}
                    onChange={(e) => setHasStairs(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {hasStairs === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-200">
                    <label className={labelClass}>Number of Stairs:</label>
                    <select 
                      value={stairsCount}
                      onChange={(e) => setStairsCount(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">Select # of Stairs</option>
                      {[...Array(20)].map((_, i) => (
                        <option key={i+1} value={i+1}>{i+1}</option>
                      ))}
                    </select>
                    
                    <label className={`${labelClass} mt-2`}>Stairs Material:</label>
                    <select 
                      value={stairsMaterial}
                      onChange={(e) => setStairsMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Material</option>
                      <option value="Composite">Composite</option>
                      <option value="Pressure Treated">Pressure Treated</option>
                    </select>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className={labelClass}>Skirting:</label>
                  <select 
                    value={hasSkirting}
                    onChange={(e) => setHasSkirting(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {hasSkirting === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-200">
                    <label className={labelClass}>Skirting Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={skirtingFeet}
                        onChange={(e) => setSkirtingFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                    
                    <label className={`${labelClass} mt-2`}>Skirting Material:</label>
                    <select 
                      value={skirtingMaterial}
                      onChange={(e) => setSkirtingMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Material</option>
                      <option value="Lattice">Lattice</option>
                      <option value="Smart Board">Smart Board</option>
                      <option value="Pressure Treated">Pressure Treated</option>
                    </select>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className={labelClass}>Fence:</label>
                  <select 
                    value={hasFence}
                    onChange={(e) => setHasFence(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {hasFence === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-200">
                    <label className={labelClass}>Fence Linear Feet:</label>
                    <div className="flex">
                      <input 
                        type="number" 
                        value={fenceFeet}
                        onChange={(e) => setFenceFeet(e.target.value)}
                        className={`${inputClass} mr-2`}
                        placeholder="Value"
                      />
                      <div className="w-1/4 py-2 px-3 bg-gray-100 border border-gray-300 text-gray-600 text-center">
                        LFT
                      </div>
                    </div>
                    
                    <label className={`${labelClass} mt-2`}>Fence Type:</label>
                    <select 
                      value={fenceMaterial}
                      onChange={(e) => setFenceMaterial(e.target.value)}
                      className={selectClass}
                      required
                    >
                      <option value="">Select Fence Type</option>
                      <option value="Pressure Treated 4x4 Posts">Pressure Treated 4x4 Posts</option>
                      <option value="Pressure Treated 4x6 Posts">Pressure Treated 4x6 Posts</option>
                      <option value="Cedar 4x4 Posts">Cedar 4x4 Posts</option>
                      <option value="Cedar 4x6 Posts">Cedar 4x6 Posts</option>
                    </select>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className={labelClass}>Gate:</label>
                  <select 
                    value={hasGate}
                    onChange={(e) => setHasGate(e.target.value)}
                    className={selectClass}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                {hasGate === 'Yes' && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-200">
                    <label className={labelClass}>Number of Gates:</label>
                    <select 
                      value={gateCount}
                      onChange={(e) => setGateCount(e.target.value)}
                      className={selectClass}
                    >
                      <option value="">Select # of Gates</option>
                      {[...Array(6)].map((_, i) => (
                        <option key={i+1} value={i+1}>{i+1}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="mb-4">
                  <label className={labelClass}>RV Gate:</label>
                  <select 
                    value={hasRVGate}
                    onChange={(e) => setHasRVGate(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className={labelClass}>Estimated Price:</label>
                  <div className="py-2 px-3 bg-yellow-100 border border-yellow-200 font-bold text-lg">
                    {estimatedPrice}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Price provided does not include GST and is only an estimate. An in-person quotation is still required and may result in prices different than provided.
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className={labelClass}>Your low monthly payment:</label>
                  <div className="py-2 px-3 bg-beaver-red/10 border border-beaver-red/20 font-bold text-lg text-beaver-red">
                    {monthlyPayment}
                  </div>
                </div>
              </form>
            </div>
            
            <div className="bg-red-600 text-white p-4">
              <h3 className="text-xl font-bold mb-1 uppercase font-secondary">Claim This Estimate</h3>
              <p className="text-sm mb-4">Book Your Site Visit - Get A Professional Quote</p>
              
              {submitSuccess ? (
                <div className="bg-green-600 text-white p-4 rounded shadow-inner">
                  <p className="font-bold">Thank you for your submission!</p>
                  <p>We have sent you a copy of your estimate to your email. A representative will contact you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} ref={formRef}>
                  {submitError && (
                    <div className="bg-yellow-600 text-white p-3 mb-4 rounded font-bold">
                      <p>{submitError}</p>
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <input 
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="firstName"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <input 
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="lastName"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <input 
                      type="tel"
                      placeholder="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="phone"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <input 
                      type="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-2 border border-gray-300 text-black"
                      required
                      disabled={isSubmitting}
                      name="email"
                    />
                  </div>
                  
                  <button 
                    type="submit"
                    className="w-full bg-black text-white font-bold py-2 px-4 hover:brightness-125 transition-all disabled:bg-gray-500 uppercase tracking-widest"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Estimate Request'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* NEW supporting SEO content */}
          <div className="max-w-4xl mx-auto mt-16 space-y-8 text-lg text-gray-700 leading-relaxed font-text">
            <h2 className="text-3xl font-bold text-gray-900 font-secondary border-b pb-3 tracking-tight">How Our Deck Cost Calculator Works</h2>
            <p>
              Our deck cost calculator provides an estimated project price based on your deck size, selected decking material, and additional features. Enter your deck square footage and choose the options that apply to your project to see an estimated cost instantly.
            </p>
            <p>The calculator considers:</p>
            <ul className="list-disc pl-6 space-y-2 marker:text-beaver-green">
              <li>Deck square footage</li>
              <li>Decking material</li>
              <li>Railing length and material</li>
              <li>6-foot privacy wall</li>
              <li>4-foot-wide stairs</li>
              <li>Skirting</li>
              <li>Fence</li>
              <li>Standard gates</li>
              <li>RV gate</li>
            </ul>
            <p>
              Your selections are combined to generate an estimated project price. The estimate is intended for budgeting purposes and does not replace an in-person quotation. For a detailed breakdown of these costs, read our comprehensive guide on <a href="/blog/5" onClick={(e) => handleNavigation(e, '/blog/5')} className="text-beaver-green font-bold hover:underline">how much a deck costs in Calgary</a>.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-3 tracking-tight">What Does the Deck Cost Calculator Include?</h2>
            <p>The calculator allows you to build an estimate based on the components you want included in your project.</p>

            <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary tracking-tight">Decking Material</h3>
            <p>Choose from the three decking materials currently available in the calculator:</p>
            <ul className="list-disc pl-6 space-y-2 marker:text-beaver-green">
              <li>Vinyl decking</li>
              <li>Pressure-treated wood</li>
              <li>Composite decking</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary tracking-tight">Railing</h3>
            <p>If your deck requires railing, you can enter the required linear footage and select:</p>
            <ul className="list-disc pl-6 space-y-2 marker:text-beaver-green">
              <li>Glass railing</li>
              <li>Aluminum picket</li>
              <li>Pressure-treated wood</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary tracking-tight">Privacy Wall</h3>
            <p>Add a 6-foot-high privacy wall and enter the required linear footage.</p>

            <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary tracking-tight">Stairs</h3>
            <p>Add 4-foot-wide stairs, select the number of stairs, and choose:</p>
            <ul className="list-disc pl-6 space-y-2 marker:text-beaver-green">
              <li>Composite</li>
              <li>Pressure-treated</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary tracking-tight">Skirting</h3>
            <p>If you want skirting around the deck, enter the required linear footage and select:</p>
            <ul className="list-disc pl-6 space-y-2 marker:text-beaver-green">
              <li>Lattice</li>
              <li>Smart Board</li>
              <li>Pressure-treated</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary tracking-tight">Fence and Gates</h3>
            <p>The calculator can also include fence and gate components as part of your project estimate.</p>
            <p>Fence options include:</p>
            <ul className="list-disc pl-6 space-y-2 marker:text-beaver-green">
              <li>Pressure-treated 4x4 posts</li>
              <li>Pressure-treated 4x6 posts</li>
              <li>Cedar 4x4 posts</li>
              <li>Cedar 4x6 posts</li>
            </ul>
            <p>You can also add standard gates or an RV gate.</p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-3 tracking-tight">What Affects Your Deck Cost?</h2>
            <p>Your estimated deck price changes based on the size of the deck and the features selected in the calculator. The main factors include:</p>
            <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
              <li><strong>Deck size:</strong> Larger decks require more decking material and structural components.</li>
              <li><strong>Deck material:</strong> Different decking materials have different costs.</li>
              <li><strong>Railing:</strong> Adding railing increases the estimate based on linear footage and railing material.</li>
              <li><strong>Privacy walls:</strong> The amount of privacy wall required affects the project estimate.</li>
              <li><strong>Stairs:</strong> The number of stairs and selected stair material affect the total.</li>
              <li><strong>Skirting:</strong> Additional skirting increases the estimated project cost based on linear footage and material.</li>
              <li><strong>Fence:</strong> Fence length and selected fence type are included when applicable.</li>
              <li><strong>Gates:</strong> Standard gates and RV gates are calculated separately.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-3 tracking-tight">Get a Professional Deck Estimate in Calgary</h2>
            <p>
              The calculator provides a convenient starting point for planning your deck budget, but the final project cost can vary based on the property, site conditions, design requirements, measurements, and construction details.
            </p>
            <p>
              For a project-specific quotation, contact Busy Beaver Construction to schedule a free onsite estimate.
            </p>
            <p className="text-xl font-bold text-gray-900 mt-4">
              Call <a href="tel:4032865400" className="text-beaver-red hover:underline">(403) 286-5400</a> to discuss your deck project.
            </p>
          </div>

          <div className="mt-20 border-t border-gray-100 pt-16">
            <h2 className="text-4xl font-bold text-text-dark mb-10 text-center font-primary uppercase tracking-tight">Calgary Deck Building FAQ</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
              <div>
                <h3 className="text-xl font-bold text-text-dark mb-3 font-secondary">What is the average cost to build a deck in Calgary?</h3>
                <p className="text-gray-600 leading-relaxed font-text"> residential deck construction in Calgary typically ranges from $31.00 to $55.00+ per square foot. Pressure-treated lumber is the most cost-effective entry point, while high-performance composite like TimberTech offers superior longevity against Alberta's extreme freeze-thaw cycles.</p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-text-dark mb-3 font-secondary">Do I need a building permit for my deck?</h3>
                <p className="text-gray-600 leading-relaxed font-text">In Calgary, any deck over 0.6 meters (2 feet) above grade or any deck that is structurally attached to the house requires a permit. Our team manages the entire permit application process to ensure your new outdoor space is fully compliant with local bylaws.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <AssociationsAndSuppliers />
      <Footer />
    </main>
  )
}